import { useEffect, useRef, useState } from "react";
import type { GardenSceneFeature, GardenSceneGraph } from "@shared/schema";

interface PlannedPlant {
  id?: string;
  plantSlug: string;
  worldX: number;
  worldY: number;
  spacingFt: number;
  bedLabel?: string | null;
}

interface CatalogEntry {
  slug: string;
  commonName: string;
  family: string;
}

interface Props {
  scene: GardenSceneGraph;
  plants: PlannedPlant[];
  catalog: CatalogEntry[];
  cardinalSouthDegrees: number | null;
}

const TYPE_FILL: Record<GardenSceneFeature["type"], string> = {
  bed: "#8a6a42",
  fence: "#4a3a2a",
  path: "#d6c9a8",
  tree: "#3f7a4a",
  shed: "#8a8a8a",
  existing_plant: "#6a9a4a",
  lawn: "#b7d39a",
  water: "#6ea6c9",
  obstacle: "#b05050",
  door: "#a07040",
  edge: "#666",
};

const TYPE_STROKE: Record<GardenSceneFeature["type"], string> = {
  bed: "#5a4525",
  fence: "#2a1f15",
  path: "#a99772",
  tree: "#24512e",
  shed: "#555",
  existing_plant: "#335c22",
  lawn: "#8cae6c",
  water: "#3f7a97",
  obstacle: "#7d2f2f",
  door: "#6a4525",
  edge: "#333",
};

const FAMILY_COLOR: Record<string, string> = {
  solanaceae: "#e0664a",
  brassicaceae: "#6fa86f",
  cucurbitaceae: "#d9b33c",
  fabaceae: "#7b9b56",
  amaryllidaceae: "#a06e9c",
  apiaceae: "#5a8fb5",
  asteraceae: "#c9804c",
  chenopodiaceae: "#7e5f9c",
  lamiaceae: "#4d8a6b",
  poaceae: "#b8a766",
  rosaceae: "#c16a8a",
  other: "#8a8a8a",
};

function compassLabel(degrees: number | null) {
  const s = ((degrees ?? 180) % 360 + 360) % 360;
  const dirs = ["S", "W", "N", "E"];
  const idx = Math.round(s / 90) % 4;
  return {
    top: dirs[idx],
    right: dirs[(idx + 1) % 4],
    bottom: dirs[(idx + 2) % 4],
    left: dirs[(idx + 3) % 4],
  };
}

function isCircleType(type: GardenSceneFeature["type"]) {
  return type === "tree" || type === "existing_plant";
}

export function GardenPlanView({ scene, plants, catalog, cardinalSouthDegrees }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setRenderedSize({ width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const extentW = Math.max(scene.gardenExtentFt.width, 4);
  const extentH = Math.max(scene.gardenExtentFt.height, 4);
  const renderedW = renderedSize?.width ?? 0;
  const pxPerFt = renderedW > 0 ? renderedW / extentW : 20;
  const svgWidth = extentW * pxPerFt;
  const svgHeight = extentH * pxPerFt;
  const compass = compassLabel(cardinalSouthDegrees);
  const catalogBySlug = new Map(catalog.map((c) => [c.slug, c]));

  // Convert world (x,y) to SVG (x,y). World y grows north; SVG y grows down.
  const toSvgX = (wx: number) => wx * pxPerFt;
  const toSvgY = (wy: number) => (extentH - wy) * pxPerFt;

  const renderFeature = (f: GardenSceneFeature) => {
    const x = toSvgX(f.worldX);
    const y = toSvgY(f.worldY + f.worldHeight);
    const w = f.worldWidth * pxPerFt;
    const h = f.worldHeight * pxPerFt;
    const fill = TYPE_FILL[f.type];
    const stroke = TYPE_STROKE[f.type];
    if (isCircleType(f.type)) {
      const cx = x + w / 2;
      const cy = y + h / 2;
      const r = Math.min(w, h) / 2;
      return (
        <circle
          key={f.id}
          cx={cx}
          cy={cy}
          r={r}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.2}
          opacity={0.85}
        />
      );
    }
    return (
      <rect
        key={f.id}
        x={x}
        y={y}
        width={w}
        height={h}
        fill={fill}
        stroke={stroke}
        strokeWidth={1.2}
        opacity={f.type === "lawn" || f.type === "path" ? 0.6 : 0.85}
        rx={f.type === "bed" || f.type === "shed" ? 3 : 0}
      />
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-md border bg-[#f4efe4]"
      style={{ aspectRatio: `${extentW} / ${extentH}` }}
    >
      {renderedSize ? (
        <svg
          width={renderedW}
          height={renderedSize.height}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 h-full w-full"
        >
          <defs>
            <pattern id="planview-soil" width={4} height={4} patternUnits="userSpaceOnUse">
              <rect width={4} height={4} fill="#ece2cc" />
              <circle cx={1} cy={1} r={0.5} fill="#c8b893" />
            </pattern>
            <pattern id="planview-grid" width={pxPerFt} height={pxPerFt} patternUnits="userSpaceOnUse">
              <path
                d={`M ${pxPerFt} 0 L 0 0 0 ${pxPerFt}`}
                fill="none"
                stroke="#d8c9ae"
                strokeWidth={0.5}
              />
            </pattern>
          </defs>
          <rect x={0} y={0} width={svgWidth} height={svgHeight} fill="url(#planview-soil)" />
          <rect x={0} y={0} width={svgWidth} height={svgHeight} fill="url(#planview-grid)" />

          {scene.features.map(renderFeature)}

          {plants.map((p, i) => {
            const cx = toSvgX(p.worldX);
            const cy = toSvgY(p.worldY);
            const r = Math.max(4, (p.spacingFt * pxPerFt) / 2);
            const entry = catalogBySlug.get(p.plantSlug);
            const color = FAMILY_COLOR[entry?.family ?? "other"] ?? FAMILY_COLOR.other;
            const label = (entry?.commonName ?? p.plantSlug).slice(0, 8);
            return (
              <g key={p.id ?? `${p.plantSlug}-${i}`}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={color}
                  stroke="#1b1b1b"
                  strokeWidth={0.8}
                  opacity={0.9}
                />
                {r > 12 ? (
                  <text
                    x={cx}
                    y={cy + 3}
                    textAnchor="middle"
                    fontSize={Math.min(11, r * 0.6)}
                    fill="#fff"
                    stroke="#000"
                    strokeWidth={0.3}
                    paintOrder="stroke"
                    style={{ pointerEvents: "none" }}
                  >
                    {label}
                  </text>
                ) : null}
              </g>
            );
          })}

          <g fontSize={Math.min(14, Math.max(10, pxPerFt * 0.6))} fontWeight={600} fill="#555" textAnchor="middle">
            <text x={svgWidth / 2} y={pxPerFt * 0.5}>{compass.top}</text>
            <text x={svgWidth / 2} y={svgHeight - pxPerFt * 0.2}>{compass.bottom}</text>
            <text x={pxPerFt * 0.4} y={svgHeight / 2 + 4} textAnchor="start">{compass.left}</text>
            <text x={svgWidth - pxPerFt * 0.4} y={svgHeight / 2 + 4} textAnchor="end">{compass.right}</text>
          </g>
        </svg>
      ) : null}
    </div>
  );
}
